/**
 * Category Cockpit — screen 1: every category of the selected site's sector.
 *
 * Three queues (need a person · ready to generate · waiting for research), a totals strip,
 * the filters of the prototype (queue, site, readiness tier, research state, search) and one
 * row per category with its next action. Everything on this page is derived by the backend
 * from the tables that hold it; nothing is computed from the rows shown.
 *
 * Plan: docs/reports/20260922-1056-category-cockpit-admin-integration-plan.md
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCategories, type CategoryList, type CategoryRow, type Queue } from '@/services/categoryCockpitService';
import { Shell, useCockpit } from './components/Shell';
import { Dots, ErrorBox, Loading, StatePill, Tag, Src } from './components/bits';
import { catUrl, fmt, gapsOpen, leaf, parent, sinceLabel, sitesOf, sum } from './lib/format';

const QUEUES: Array<{ k: Queue; l: string; d: string; cls: string }> = [
  { k: 'action', l: 'Need a decision or a fix', d: 'blocked, waiting on a person, or stale', cls: 'a' },
  { k: 'ready', l: 'Ready to generate', d: 'a site passes the readiness gates', cls: 'r' },
  { k: 'research', l: 'Waiting for research', d: 'never researched, or nothing fresh', cls: 't' },
];
type SortKey = 'name' | 'products' | 'pages' | 'gaps' | 'ready' | 'action' | 'since';
const RANK: Record<string, number> = { blocked: 0, attention: 1, todo: 2, ready: 3, off: 4 };

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

/** the sort key of a row, exported for the unit test */
export function sortKey(c: CategoryRow, s: SortKey, now: number): string | number {
  const sites = sitesOf(c.sites);
  switch (s) {
    case 'name': return leaf(c.category_path).toLowerCase();
    case 'products': return -sum(sites, (x) => x.products);
    case 'pages': { const o = c.outcome_totals; return -(o.rejected * 1e6 + (o.needs_review + o.pending_review) * 1e3 + o.published); }
    case 'gaps': return -sum(sites, (x) => gapsOpen(x));
    case 'ready': return -(c.derived.dots.filter((x) => x === 'f').length * 2 + c.derived.dots.filter((x) => x === 'h').length);
    case 'since': { const d = c.last_researched_at ? Math.round((now - new Date(c.last_researched_at).getTime()) / 864e5) : 9999; return -d; }
    default: return RANK[c.derived.next_action.k] * 1000 - c.derived.attention;
  }
}

export function filterRows(list: CategoryList, f: { queue: Queue | 'all'; site: string; tier: string; state: string; q: string }): CategoryRow[] {
  let cats = list.categories.slice();
  if (f.queue !== 'all') cats = cats.filter((c) => c.derived.queues.includes(f.queue as Queue));
  if (f.site !== 'all') cats = cats.filter((c) => c.sites.some((s) => s.site_id === f.site && (s.products || s.tier || gapsOpen(s))));
  // WHEN A SITE IS SELECTED, EVERY OTHER FILTER MUST SPEAK ABOUT THAT SITE. The readiness filter
  // asked "does ANY site hold this tier", so picking a site and AAA kept a category that is C on
  // the chosen site and AAA on another, and "unscored" asked whether EVERY site is unscored, which
  // hid a category unscored on the chosen site (codex-reviewer, PR #6457). A filter that reads
  // rows the user has just excluded is not a filter.
  const scoped = (c: CategoryRow) => (f.site === 'all' ? c.sites : c.sites.filter((s) => s.site_id === f.site));
  if (f.tier !== 'all') cats = cats.filter((c) => (f.tier === 'unscored' ? !scoped(c).some((s) => s.tier) : scoped(c).some((s) => s.tier === f.tier)));
  if (f.state !== 'all') cats = cats.filter((c) => (f.state === 'fresh' ? c.derived.states.fresh === 10 : (c.derived.states as Record<string, number>)[f.state] > 0));
  if (f.q) cats = cats.filter((c) => c.category_path.toLowerCase().includes(f.q));
  return cats;
}

export default function CategoryCockpitList() {
  return (
    <Shell active="cats">
      <ListBody />
    </Shell>
  );
}

function ListBody() {
  const { sectorId, sources } = useCockpit();
  const navigate = useNavigate();
  const [list, setList] = useState<CategoryList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<Queue | 'all'>('action');
  const [site, setSite] = useState('all');
  const [tier, setTier] = useState('all');
  const [state, setState] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('action');
  const [dir, setDir] = useState(1);

  useEffect(() => {
    if (sectorId == null) return undefined;
    const ac = new AbortController();
    setLoading(true); setError(null); setList(null);
    fetchCategories(sectorId, ac.signal)
      .then((d) => { setList(d); setLoading(false); })
      .catch((err) => { if (isAbort(err)) return; setError(err?.message || String(err)); setLoading(false); });
    return () => ac.abort();
  }, [sectorId]);

  const now = Date.now();
  const rows = useMemo(() => {
    if (!list) return [];
    const cats = filterRows(list, { queue, site, tier, state, q: q.toLowerCase() });
    cats.sort((a, b) => { const x = sortKey(a, sort, now); const y = sortKey(b, sort, now); return (x < y ? -1 : x > y ? 1 : 0) * dir; });
    return cats;
  }, [list, queue, site, tier, state, q, sort, dir, now]);

  const onSort = (k: SortKey) => { if (sort === k) setDir(-dir); else { setSort(k); setDir(1); } };
  const th = (k: SortKey, label: string, cls = '', title?: string) => (
    <th className={`${cls} ${sort === k ? 'on' : ''}`} onClick={() => onSort(k)} data-tip={title} scope="col">{label}{sort === k ? (dir > 0 ? ' ↓' : ' ↑') : ''}</th>
  );

  if (loading) return <Loading label="Reading the sector’s categories (the first read of a sector takes a few seconds, then it is cached ten minutes)…" />;
  if (error) return <ErrorBox title="The categories could not be loaded" msg={error} />;
  if (!list) return null;
  const T = list.totals;
  const fact = (v: React.ReactNode, l: string, src: string, cls = '') => <span className={cls} data-tip={src}><b>{v}</b> {l}</span>;

  return (
    <>
      <h1 className="pmk-h1">{list.sector.name}</h1>
      <p className="pmk-sub">{list.categories.length} categories in the book of this sector, {list.sites.length} site{list.sites.length > 1 ? 's' : ''}. Each row is one category; open it to read its chapters, or follow the next action.</p>

      <div className="pmk-queues">
        {QUEUES.map((qq) => (
          <button key={qq.k} type="button" className={`pmk-queue ${qq.cls} ${queue === qq.k ? 'on' : ''}`} onClick={() => setQueue(queue === qq.k ? 'all' : qq.k)} aria-pressed={queue === qq.k}>
            <span className="n">{T.queues[qq.k]}</span><span className="l">{qq.l}</span><span className="d">{qq.d}</span>
          </button>
        ))}
      </div>

      <div className="pmk-totals">
        {fact(`${T.ready_pairs} / ${T.scored_pairs}`, 'site × category pairs ready', 'category_readiness_scores · is_ready', T.ready_pairs ? 'ok' : '')}
        {fact(`${fmt(T.enriched)} / ${fmt(T.products)}`, 'products enriched', `product_catalog · ${T.sites} sites in sector`)}
        {fact(fmt(T.pages_published), 'pages published', 'aeo_page_generations · status = published')}
        {fact(fmt(T.pages_in_review), 'in review', 'needs_review + pending_review')}
        {fact(T.pages_rejected, 'rejected', 'aeo_page_generations · rejected', T.pages_rejected ? 'bad' : '')}
        {fact(fmt(T.gaps_open), 'gaps waiting on a person', 'semantic_gap_queue · pending + suggested + approved + needs_expert', T.gaps_open ? 'warn' : '')}
        {fact(T.stale_cells, 'stale aspect cells', `stale_after < now · ${T.stale_categories} categories`, T.stale_cells ? 'warn' : '')}
        {fact(T.never_cells, 'never-researched cells', `last_researched_at is null · ${T.never_categories} categories, ${T.error_cells} in error`, T.never_cells ? 'bad' : '')}
        <Src>category_characteristics · sector_id {list.sector.id} · read from the {list._meta.source} in {list._meta.computed_ms} ms{list.cached ? ' (cached)' : ''} · computed {list.computed_at.slice(0, 16).replace('T', ' ')} UTC · hover a number for its table</Src>
      </div>

      <div className="pmc-panel">
        <div className="pmk-filters">
          <span className="pmk-seg">
            <button type="button" className={queue === 'all' ? 'on' : ''} onClick={() => setQueue('all')}>All {list.categories.length}</button>
            <button type="button" className={queue === 'action' ? 'on' : ''} onClick={() => setQueue('action')}>Needs a person</button>
          </span>
          <label>Site
            <select className="pmc-select" value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="all">all</option>
              {list.sites.map((s) => <option key={s.id} value={s.id}>{s.name}{s.assigned_to_sector ? '' : ' (scored, not assigned)'}</option>)}
            </select>
          </label>
          <label>Readiness
            <select className="pmc-select" value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="all">any</option>
              {['AAA', 'AA', 'A', 'B', 'C', 'unscored'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Research
            <select className="pmc-select" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="all">any state</option>
              <option value="fresh">all fresh</option>
              <option value="stale">has stale aspects</option>
              <option value="never">has never-researched aspects</option>
              <option value="error">research error</option>
            </select>
          </label>
          <input className="pmc-input" type="search" placeholder="Search a category…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search a category" />
          <span className="pmc-grow" />
          <span className="pmc-small pmc-mute">{rows.length} of {list.categories.length}</span>
        </div>
        <div className="pmk-tablewrap">
          <table className="pmk-cats">
            <thead>
              <tr>
                {th('name', 'Category')}{th('products', 'Products', 'r', 'enriched / total, on the sites of the sector')}{th('pages', 'Pages', 'r', 'published · in review · rejected')}{th('gaps', 'Gaps', 'r', 'semantic gaps waiting on a person')}{th('ready', 'Ready', '', 'have · good · tested · versioned')}{th('action', 'Next action')}{th('since', 'Last research')}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const sites = sitesOf(c.sites);
                const prods = sum(sites, (s) => s.products); const enr = sum(sites, (s) => s.enriched); const kids = sum(sites, (s) => s.products_in_children); const open = sum(sites, (s) => gapsOpen(s));
                const a = c.derived.next_action; const o = c.outcome_totals; const fl = c.derived.flags; const bl = c.blast.global;
                const to = catUrl(c.category_path, a.ch === 'inbox' ? undefined : a.ch);
                return (
                  <tr key={c.category_path} onClick={() => navigate(catUrl(c.category_path))}>
                    <td>
                      <span className="name">{leaf(c.category_path)}</span>
                      {c.research_error && <Tag kind="bad" title={c.research_error}>error</Tag>}
                      <span className="path" title={c.category_path}>{parent(c.category_path) || '—'}</span>
                    </td>
                    <td className="r">
                      {prods ? <><b>{fmt(enr)}</b> <span className="sub">/ {fmt(prods)}</span></> : <span className="sub">—</span>}
                      {kids ? <div className="sub">+{fmt(kids)} in children</div> : null}
                      {bl.sites > 1 ? <div className="sub pmk-warn" data-tip={`all sectors: ${bl.sites} sites · ${fmt(bl.products)} products`}>{bl.sites} sites · {fmt(bl.products)}</div> : null}
                    </td>
                    <td className="r">
                      {!o.total ? <span className="sub">—</span>
                        : !(o.published + o.needs_review + o.pending_review + o.rejected) ? <span className="sub">{fmt(o.draft + o.archived)} draft</span>
                          : <><b data-tip="published">{fmt(o.published)}</b> <span className="sub" data-tip="in review">· {fmt(o.needs_review + o.pending_review)}</span> <span className={`sub ${o.rejected ? 'pmk-bad' : ''}`} data-tip="rejected">· {o.rejected}</span></>}
                    </td>
                    <td className="r">{open ? <b>{fmt(open)}</b> : <span className="sub">0</span>}</td>
                    <td><Dots dots={c.derived.dots} /></td>
                    <td className="actc">
                      <div className="act">
                        <StatePill k={a.k} title={fl.map((f) => f[1]).join(' · ') || a.why}>{a.t}</StatePill>
                        {a.none
                          ? <span className="why">{a.why}</span>
                          : a.ch === 'inbox'
                            ? <a className="why" href="/category-cockpit/decide" onClick={(e) => e.stopPropagation()}>{a.why}</a>
                            : <a className="why" href={to} onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(to); }}>{a.why}</a>}
                        {fl.length > 1 && (
                          <span className="pmk-flags">
                            {fl.filter((f) => f[1] !== a.why).slice(0, 2).map((f) => <i key={f[1]} className={f[0]}>{f[1]}</i>)}
                            {fl.length > 3 && <i data-tip={fl.slice(3).map((f) => f[1]).join(' · ')}>+{fl.length - 3}</i>}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="since">
                      {sinceLabel(c.last_researched_at, now)}
                      {c.research_source === 'product_grounded' && c.sample_product_count ? <div className="sub">grounded on {c.sample_product_count} products</div> : c.last_researched_at ? <div className="sub">model only</div> : null}
                      {sources && <div className="sub">attention {c.derived.attention}</div>}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && <tr><td colSpan={7}><div className="pmc-empty"><b>Nothing here.</b> No category matches this queue and these filters.</div></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="pmc-footnote">
          Sort “Next action” = state rank, then attention = (never × 3 + error × 3 + stale × 1) × log₁₀(products incl. children + 2) + a weight per not-ready site + open gaps (capped). A sort key for the human, not a score stored anywhere.
        </div>
      </div>
    </>
  );
}
